import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import LevelEditor from "../components/LevelEditor";
import ImportJsonPopup from "../components/ImportJsonPopup";
import "./LevelsPage.css";

// Firestore docs store `grid` as `gridJson` (a JSON string) since Firestore
// rejects arrays-of-arrays. Convert back to the number[][] shape the
// editor/game code works with everywhere else.
function docToLevel(data) {
  const { gridJson, ...rest } = data;
  return { ...rest, grid: gridJson ? JSON.parse(gridJson) : [] };
}

const NEW_LEVEL_TEMPLATE = {
  isNew: true,
  id: "",
  name: "",
  rows: 10,
  cols: 9,
  dropRate: 0.18,
  grid: Array.from({ length: 10 }, () => Array(9).fill(0)),
};

const LevelsPage = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLevel, setEditingLevel] = useState(null);
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [error, setError] = useState("");

  const fetchLevels = async () => {
    setLoading(true);
    setError("");
    try {
      const snapshot = await getDocs(collection(db, "levels"));
      const list = snapshot.docs.map((d) => docToLevel(d.data()));
      list.sort((a, b) => a.id - b.id);
      setLevels(list);
    } catch (e) {
      console.error(e);
      setError("Failed to load levels: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const handleSave = async (levelData) => {
    // Firestore rejects arrays-of-arrays ("Nested arrays are not
    // supported"), so `grid` (number[][]) is stored as a JSON string
    // instead - see docToLevel() for the reverse conversion on read.
    const { grid, ...rest } = levelData;
    await setDoc(doc(db, "levels", String(levelData.id)), {
      ...rest,
      gridJson: JSON.stringify(grid),
      updatedAt: serverTimestamp(),
    });
    setEditingLevel(null);
    fetchLevels();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete level ${id}? This can't be undone.`)) return;
    await deleteDoc(doc(db, "levels", String(id)));
    setEditingLevel(null);
    fetchLevels();
  };

  const handleImport = (levelData) => {
    setShowImportPopup(false);
    setEditingLevel({ ...levelData, isNew: !levels.some((l) => l.id === levelData.id) });
  };

  if (editingLevel) {
    return (
      <div className="page-content">
        <LevelEditor
          initialData={editingLevel}
          existingIds={levels.map((l) => l.id)}
          onSave={handleSave}
          onCancel={() => setEditingLevel(null)}
          onDelete={editingLevel.isNew ? undefined : handleDelete}
        />
      </div>
    );
  }

  return (
    <div className="page-content">
      <h1>Game Levels</h1>
      <div className="actions-bar">
        <button className="btn btn-primary" onClick={() => setEditingLevel(NEW_LEVEL_TEMPLATE)}>
          Add New Level
        </button>
        <button className="btn" onClick={() => setShowImportPopup(true)}>
          Import JSON
        </button>
      </div>

      {error && <p style={{ color: "var(--danger-color)" }}>{error}</p>}
      {loading ? (
        <p>Loading levels...</p>
      ) : levels.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>
          No levels in Firestore yet. The game falls back to its bundled/procedural
          levels until you add some here.
        </p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Size</th>
                <th>Drop rate</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {levels.map((level) => (
                <tr key={level.id}>
                  <td>{level.id}</td>
                  <td>{level.name}</td>
                  <td>
                    {level.rows}×{level.cols}
                  </td>
                  <td>{level.dropRate}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-primary" onClick={() => setEditingLevel({ ...level, isNew: false })}>
                        Edit
                      </button>
                      <button className="btn btn-danger" onClick={() => handleDelete(level.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showImportPopup && (
        <ImportJsonPopup onImport={handleImport} onClose={() => setShowImportPopup(false)} />
      )}
    </div>
  );
};

export default LevelsPage;
